import { z } from 'zod';
import { esPrimerSemestre, FASES_DEL_PROGRAMA } from './fases.js';
/**
 * Parámetros operativos del seguimiento.
 *
 * **Todos son configuración, y varios son provisorios.** Los que salen del
 * protocolo están anotados con su fuente; los que Fundación todavía no definió
 * están marcados como tales y su default es una propuesta, no un criterio
 * clínico. `seguimiento.html` §5 lo dice explícitamente: "la frecuencia exacta de
 * contactos por etapa, las ventanas de silencio toleradas y los umbrales de
 * ajuste de intensidad se definen según los protocolos clínicos de Fundación
 * Trauma".
 *
 * Están acá y no dispersos en el job para que cambiar uno sea cambiar un valor, y
 * para que se vea de un vistazo cuáles son de Fundación y cuáles son nuestros
 * mientras tanto.
 */
export const configuracionDeCadenciaSchema = z.strictObject({
    /**
     * Ventana horaria de envío, en hora local. **Del protocolo** (`6-meses.html`:
     * "09:00–11:00 hs según zona horaria del paciente").
     */
    ventanaEnvioDesdeHora: z.number().int().min(0).max(23),
    ventanaEnvioHastaHora: z.number().int().min(1).max(24),
    /**
     * Cuánto antes de la teleconsulta tiene que estar hecha su evaluación.
     * **Del protocolo**: "evaluación sincronizada ≤3 días antes de teleconsulta".
     */
    diasMaximosEntreEvaluacionYTeleconsulta: z.number().int().positive(),
    /**
     * Cuánto tiempo sigue siendo respondible un ítem después de su día.
     *
     * **PROVISORIO.** El protocolo fija el recontacto (24 h en el primer semestre,
     * 72 en el segundo) pero no dice cuándo un check-in deja de aceptarse. La
     * propuesta es holgada a propósito: un ítem que vence rápido convierte en
     * "perdido" lo que fue una demora de fin de semana, y eso desinfla la
     * adherencia sin que haya pasado nada clínico.
     */
    horasDeVentanaDeRespuestaPrimerSemestre: z.number().int().positive(),
    horasDeVentanaDeRespuestaSegundoSemestre: z.number().int().positive(),
    /**
     * A partir de cuántas horas sin respuesta hay silencio, **por fase**.
     *
     * **Cerrado por Fundación en agosto de 2026** (`semaforos-etiquetas.md` §3.2,
     * tabla ratificada el 6/8), con rango configurable por institución. Las siete
     * versiones que convivían antes quedaron reemplazadas por esta.
     *
     * Ojo con confundirlo con el **recordatorio de check-in**, que es otra cosa y
     * la separación quedó validada: el recordatorio es un re-aviso liviano al
     * paciente que no escala ni cambia estados. Las 24 h del protocolo son de eso,
     * no de silencio.
     */
    horasParaSilencioPorFase: z.record(z.enum(FASES_DEL_PROGRAMA), z.number().int().positive()),
    /**
     * Cuánto se espera entre un nivel del protocolo de silencio y el siguiente.
     *
     * **PROVISORIO.** La tabla de Fundación fija el umbral de cada fase, no el
     * espaciado entre los dos niveles. Es parte de lo que el pendiente del estado
     * `gris` tiene que cerrar: "n intentos y ventana".
     */
    horasEntreNivelesDeSilencio: z.number().int().positive(),
});
/**
 * Los defaults.
 *
 * Lo marcado **PROVISORIO** arriba está esperando definición de Fundación
 * (`dev/pendientes-fundacion.md` §0). Ninguno de esos valores enciende un color
 * ni dispara un aviso: sólo deciden cuándo el sistema vuelve a hablarle al
 * paciente y cuándo deja de esperar una respuesta.
 */
export const CADENCIA_POR_DEFECTO = Object.freeze({
    ventanaEnvioDesdeHora: 9,
    ventanaEnvioHastaHora: 11,
    diasMaximosEntreEvaluacionYTeleconsulta: 3,
    horasDeVentanaDeRespuestaPrimerSemestre: 72,
    horasDeVentanaDeRespuestaSegundoSemestre: 120,
    /**
     * La tabla cerrada de `semaforos-etiquetas.md` §3.2. El cierre y la extensión
     * comparten fila —"5 a 7 días"— y se toma el extremo bajo: insistir un día
     * antes molesta menos que perder a alguien dos días más tarde.
     */
    horasParaSilencioPorFase: {
        M1_M3: 48,
        M4_M6: 72,
        M7_M11: 120,
        M12: 120,
        EXTENSION: 120,
    },
    horasEntreNivelesDeSilencio: 24,
});
/** Cuánto tiempo sigue abierto un ítem de esta fase. */
export function horasDeVentanaDeRespuesta(fase, config) {
    return esPrimerSemestre(fase)
        ? config.horasDeVentanaDeRespuestaPrimerSemestre
        : config.horasDeVentanaDeRespuestaSegundoSemestre;
}
/** A partir de cuándo la falta de respuesta es silencio, en esta fase. */
export function horasParaSilencio(fase, config) {
    return (config.horasParaSilencioPorFase[fase] ??
        CADENCIA_POR_DEFECTO.horasParaSilencioPorFase[fase] ??
        72);
}
//# sourceMappingURL=configuracion.js.map